import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PlantCard from "@/components/PlantCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Plus } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function Library() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState(["All"]);
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/catalog/categories").then((r) => setCats(r.data.categories || ["All"]));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (cat !== "All") params.set("category", cat);
    if (q) params.set("q", q);
    api.get(`/catalog?${params.toString()}`).then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, [cat, q]);

  const addToGarden = async (p) => {
    try {
      const { id, ...rest } = p;
      await api.post("/garden", { catalog_id: id, ...rest });
      toast.success(`${p.name} added to your garden.`);
    } catch {
      toast.error("Could not add.");
    }
  };

  return (
    <div className="space-y-8" data-testid="library">
      <div>
        <div className="label-overline">Plant library</div>
        <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tighter mt-1">
          A seeded almanac of green things.
        </h1>
        <p className="text-foreground/70 mt-3 max-w-xl">Browse, search, and adopt plants into your garden.</p>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name…"
            className="pl-9"
            data-testid="library-search"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {cats.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`cat-${c}`}
              className={`px-3 py-1.5 text-xs rounded-md transition-all duration-300 ${
                cat === c
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted/60 text-foreground/70 hover:bg-muted"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        <Link to="/garden" className="md:ml-auto">
          <Button variant="outline" data-testid="goto-mygarden">View My Garden</Button>
        </Link>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Gathering seeds…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-muted-foreground italic">No matches in this row of the almanac.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {items.map((p) => (
            <div key={p.id} className="relative">
              <PlantCard plant={p} compact={false} />
              <Button
                size="sm"
                onClick={() => addToGarden(p)}
                className="absolute top-2 right-2 bg-accent text-accent-foreground hover:bg-accent/90 h-7 px-2 gap-1"
                data-testid={`add-${p.id}`}
              >
                <Plus className="w-3 h-3" /> Add
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
